import type { BuildingLayerItem } from "features/dt-basic-setup/single-building/model/types";

import { type Dispatch, useEffect } from "react";
import { isInputLikeElement } from "utils/general";

import type { LayerStepAction } from "../types";

type LayerDispatch = Dispatch<LayerStepAction>;

export function useClampActiveLayerEffect(layers: BuildingLayerItem[], activeLayerId: string, dispatch: LayerDispatch) {
  useEffect(() => {
    dispatch({ layers, type: "CLAMP_ACTIVE_LAYER" });
  }, [layers, activeLayerId, dispatch]);
}

export function useClampActiveSubLayerEffect(
  activeLayer: BuildingLayerItem | undefined,
  activeSubLayerId: string,
  dispatch: LayerDispatch
) {
  useEffect(() => {
    if (!activeLayer) return;
    dispatch({ subLayers: activeLayer.subLayers, type: "CLAMP_ACTIVE_SUBLAYER" });
  }, [activeLayer, activeSubLayerId, dispatch]);
}

export function useEndDrawingOnActiveLayerOrSubLayerChangeEffect(
  activeLayerId: string,
  activeSubLayerId: string,
  endDrawingSession: () => void
) {
  useEffect(() => {
    endDrawingSession();
  }, [activeSubLayerId, activeLayerId, endDrawingSession]);
}

export function useEndDrawingWhenCannotDrawEffect(canDraw: boolean, endDrawingSession: () => void) {
  useEffect(() => {
    if (!canDraw) endDrawingSession();
  }, [canDraw, endDrawingSession]);
}

export function useFinishDrawingOnEnterKeyEffect(canDraw: boolean, isDrawing: boolean, finishDrawing: () => void) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (!isDrawing || !canDraw) return;
      const target = event.target as HTMLElement | null;
      if (isInputLikeElement(target)) return;
      event.preventDefault();
      finishDrawing();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canDraw, finishDrawing, isDrawing]);
}

export function useSyncNewSubLayerKeyToLayerPresetEffect(
  activeLayer: BuildingLayerItem | undefined,
  activeLayerId: string,
  dispatch: LayerDispatch
) {
  useEffect(() => {
    if (!activeLayer) return;
    dispatch({ preset: activeLayer.preset, type: "ENSURE_NEW_SUB_LAYER_KEY" });
  }, [activeLayer, activeLayerId, dispatch]);
}
